// Copyright 2026 The Simlin Authors. All rights reserved.
// Use of this source code is governed by the Apache License,
// Version 2.0, that can be found in the LICENSE file.

// pattern: Functional Core

import * as React from 'react';

import type { SaveStatus } from './save-status';

import styles from './SaveControl.module.css';

interface SaveControlProps {
  /** Outcome of the most recent save, as reported by the host's onSave. */
  status: SaveStatus;
  /**
   * The viewer cannot save (signed out, or someone else's model). The Save
   * button is hidden and a plain "View only" note explains why; it is driven
   * by the live prop so it disappears as soon as the host learns the viewer
   * owns the model (e.g. the session finishes loading after the project).
   */
  readOnly: boolean;
  /**
   * False when the host stores nothing (the backend-free demo). Its onSave
   * still returns a version for the Editor's bookkeeping, so without this
   * flag the toolbar would claim "Saved". Instead the Save button is hidden
   * and a calm "Demo" note says changes aren't kept; `status` is ignored.
   */
  persists: boolean;
  /** Save now. Also used by Retry -- both re-send the current model. */
  onSave: () => void;
}

/**
 * Toolbar Save button plus the save status beside it: "Saving...", "Saved",
 * "Unsaved changes", or "Not saved" with a plain reason and a Retry button. The control holds no
 * state; the Editor owns `status` and only sets 'saved' after the server
 * confirms, so this component can never show a save that didn't happen.
 */
export function SaveControl(props: SaveControlProps): React.ReactElement {
  const { status, readOnly, persists, onSave } = props;

  if (!persists) {
    return (
      <span
        className={styles.viewOnly}
        title="This is a demo. Your changes stay in this page and are lost when you leave."
      >
        Demo &mdash; changes aren&rsquo;t saved
      </span>
    );
  }

  if (readOnly) {
    return (
      <span className={styles.viewOnly} title="You can look around and try changes, but they won't be saved.">
        View only &mdash; changes won&rsquo;t be saved
      </span>
    );
  }

  const saving = status.kind === 'saving';

  return (
    <div className={styles.saveControl}>
      <button type="button" className={styles.saveButton} onClick={onSave} disabled={saving}>
        Save
      </button>
      {/* aria-live so screen readers hear "Saved" / "Not saved" as it happens. */}
      <span className={styles.status} role="status" aria-live="polite">
        {status.kind === 'saving' && <span className={styles.saving}>Saving&hellip;</span>}
        {status.kind === 'saved' && <span className={styles.saved}>Saved</span>}
        {status.kind === 'unsaved' && <span className={styles.unsaved}>Unsaved changes</span>}
        {status.kind === 'error' && (
          <>
            <span className={styles.notSaved}>Not saved</span>
            <span className={styles.reason} title={status.reason}>
              {status.reason}
            </span>
            <button type="button" className={styles.retryButton} onClick={onSave}>
              Retry
            </button>
          </>
        )}
      </span>
    </div>
  );
}
